// @ts-strict-ignore
import { Connection } from '../model3d/connection';
import { ImageTextureProcessor } from './image-texture.processor';
import { ArrayUtils } from '@shared/util/array-utils';
import { TextTextureProcessor } from './text-texture.processor';
import { lastValueFrom, mergeMap, Observable, of, Subscriber } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ArticleContainer } from '../model3d/article-container';
import { FileReadingType, FileUtils, LoadedImage } from '@shared/util/file-utils';
import { UserModel } from '@user/model/user.model';
import { CustomImageDataVO } from '@project/components/article/model/vo/custom-article.vo';
import { CustomArticleService } from '@article/service/custom-article.service';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { UndoChangeImageConnectionsOperation } from '@shared/undo/operation/undo-change-image-connections-operation';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { StringUtils } from '@shared/util/string-utils';
import { UndoRemoveImageConnectionsOperation } from '@shared/undo/operation/undo-remove-image-connections-operation';
import { Log } from '@shared/debug/log';
import { SceneManager } from '../manager/scene.manager';
import { Injectable } from '@angular/core';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ContainerService } from '../model3d/container.service';
import { ArticleMenuItemsService } from '../service/article-menu-items.service';
import { LabelingUtils } from '../model3d/util/labeling-utils';
import { ConnectionInfoUtils } from '../model3d/util/connection-info-utils';
import { MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';

@Injectable({ providedIn: 'root' })
export class LoadConnectionTextureProcessor {

  private isLoading: boolean = false;
  private loadingQueue: Observable<any>[] = [];

  constructor(
    private userModel: UserModel,
    private imageTextureProcessor: ImageTextureProcessor,
    private textTextureProcessor: TextTextureProcessor,
    private customArticleService: CustomArticleService,
    private undoManager: UndoRedoManager,
    private sceneManager: SceneManager,
    private alertHelper: AlertHelper,
    private containerService: ContainerService,
    private labelingUtils: LabelingUtils,
    private _articleMenuItemsService: ArticleMenuItemsService
  ) {}

  public cleanConnectionImageCache(connections: Connection[]): void {
    this.imageTextureProcessor.cleanConnectionImageCache(connections);
  }

  /**
   * Use this method to create and apply texture to texture connection according to the connection's meta information
   * @param connections
   */
  public loadAndApplyImagesToConnections(connections: Connection[]): void {
    this.loadAndApplyImagesToConnectionsObservable(connections).subscribe();
  }

  /**
   * Use this method to create and apply texture to texture connection according to the connection's meta information
   * @param connections
   */
  public loadAndApplyImagesToConnectionsObservable(connections: Connection[]): Observable<any> {
    if (ArrayUtils.isEmpty(connections)) {
      return of(connections);
    }
    const request: Observable<any> = this.resetTextures(connections).pipe(
      mergeMap(() => this.imageTextureProcessor.loadAndApplyImagesToConnections(connections)),
      mergeMap(() => this.textTextureProcessor.loadAndApplyTexturesToConnections(connections)),
      tap(() => {
        this.sceneManager.renderScene();
        this.isLoading = false;
      }),
      mergeMap(() => this.processNextRequest())
    );
    this.loadingQueue.push(request);
    return this.processNextRequest();
  }

  private resetTextures(connections: Connection[]): Observable<any> {
    return new Observable<any>((subscriber: Subscriber<any>) => {
      connections.forEach((connection: Connection) => {
        connection.resetTexture();
      });
      this.isLoading = true;
      subscriber.next(undefined);
      subscriber.complete();
    });
  }

  private processNextRequest(): Observable<any> {
    if (this.isLoading || ArrayUtils.isEmpty(this.loadingQueue)) {
      return of(null);
    } else {
      return this.loadingQueue.shift();
    }
  }

  /**
   * Textures will be loaded for connection, only if connection is of 'texture' type and 'Connection.isTextureLoaded === false'
   * @param articleContainers
   */
  public loadAndApplyTexturesToArticleContainers(articleContainers: ArticleContainer[]): Promise<void> {
    if (ArrayUtils.isEmpty(articleContainers)) {
      return;
    }
    return lastValueFrom(this.loadAndApplyTexturesToArticleContainersObservable(articleContainers));
  }

  public loadAndApplyTexturesToArticleContainersObservable(articleContainers: ArticleContainer[]): Observable<any> {
    if (ArrayUtils.isEmpty(articleContainers)) {
      return of(null);
    }
    return this.loadAndApplyImagesToConnectionsObservable(articleContainers.flatMap((articleContainer: ArticleContainer) => articleContainer.connections));
  }

  public async uploadAndApplyImageToConnections(connections: Connection[], cleanCallback: () => void): Promise<void> {
    if (ArrayUtils.isEmpty(connections)) {
      return;
    }
    let loadedImage: LoadedImage;
    try {
      loadedImage = await FileUtils.loadImageFile(this.alertHelper, '.jpg', '.jpeg', '.png');
    } catch (error) {
      this.alertHelper.showError("imageLoader.error.title.image_not_loaded", "imageLoader.error.message.image_not_loaded");
      Log.warn("LoadConnectionTextureProcessor: Error occurred while loading custom image", error);
      return;
    }

    if (loadedImage.image) {
      return await this.applyCustomImageDataToConnections(loadedImage, connections, cleanCallback);
    }
  }

  private createUniColorImageAsDataUrl(color: string | string[], width: number = 1, height: number = 1): string {
    const canvas: HTMLCanvasElement = document.createElement("canvas");

    const context = canvas.getContext("2d");
    // If we have more then one color, we need to reverse the order of colors, because the first color will be drawn last
    // and the last color will be drawn first, due to some internal behavior of the 3D scene rendering.
    const colors = typeof color === 'string' ? [color] : [...color].reverse();
    // For more than one color we need more than one pixel width, otherwise threejs will create a kind
    // of gradient between colors and the result will be not as expected. So we need to create a wider
    // image with more pixels for each color.
    const stretchFactor = colors.length > 1 ? 16 : 1;
    canvas.width = width * stretchFactor * colors.length;
    canvas.height = height;
    if (context) {
      for (let i=0; i<colors.length; i++) {
        context.fillStyle = colors[i];
        context.fillRect(width * stretchFactor * i, 0, width * stretchFactor, height);
      }
    }
    return canvas.toDataURL("image/png");
  }

  public async applyColorToConnections(
    color: string | string[],
    connections: Connection[],
    cleanCallback: () => void
  ): Promise<void> {
    let extension: string = "png";
    let imageData: string = this.createUniColorImageAsDataUrl(color);
    let binaryImage: HTMLImageElement = new Image();
    binaryImage.src = imageData;

    await this.applybase64EncodedImageToConnections(imageData, binaryImage, extension, connections, cleanCallback);
  }

  private async applyCustomImageDataToConnections(
    image: LoadedImage,
    connections: Connection[],
    cleanCallback: () => void
  ): Promise<void> {
    let extension = FileUtils.getFileExtension(image.file);
    let imageData = await FileUtils.readFile(image.file, FileReadingType.DATA_URL);
    let binaryImage: HTMLImageElement = image.image;

    await this.applybase64EncodedImageToConnections(imageData, binaryImage, extension, connections, cleanCallback);
  }

  private async applybase64EncodedImageToConnections(
    imageData: string,
    binaryImage: HTMLImageElement,
    extension: string,
    connections: Connection[],
    cleanCallback: () => void
  ): Promise<void> {
    const errorMessage = "LoadConnectionTextureProcessor: Error occurred while loading custom image";
    try {
      const customImageData: CustomImageDataVO = new CustomImageDataVO();
      customImageData.userId = this.userModel.user.id;
      customImageData.imageType = extension;
      customImageData.imageData = imageData.slice(imageData.indexOf(',') + 1);
      const imageHash = await lastValueFrom(this.customArticleService.saveCustomArticleImageObservable(customImageData));

      if (StringUtils.isEmpty(imageHash)) {
        Log.warn(errorMessage, new Error('Image hash is empty!'));
        return;
      }
      this.undoManager.pushUndo(new UndoChangeImageConnectionsOperation(connections, this, imageHash, this.containerService, this.labelingUtils));
      this.cleanConnectionImageCache(connections);
      ConnectionInfoUtils.updateImageHashForConnections(imageHash, connections);
      this.updateCustomImageUserId(connections);

      this.imageTextureProcessor.createAndSaveCachedObject(imageHash, binaryImage);
      connections.forEach((connection: Connection) => connection.resetTexture());
      cleanCallback();
      const loadedImage = await lastValueFrom(this.loadAndApplyImagesToConnectionsObservable(this.labelingUtils.collectAllMergedTextureConnections(connections)));
      if (loadedImage) {
        this._articleMenuItemsService.articleMenuItemsRefreshed();
      }
    } catch (exception) {
      Log.warn(errorMessage, exception);
    }
  }

  private updateCustomImageUserId(connections: Connection[]): void {
    for (const connectionCube of connections) {
      const articleUsage: ArticleUsageVO = connectionCube.articleContainer.articleUsage;
      let userId: number = CustomArticleUtils.getCustomImageUserId(articleUsage);
      if (NumberUtils.isNotNumber(userId) && this.userModel.isUserLoggedIn) {
        userId = this.userModel.user.id;
        ArticleUsageUtils.setArticleUsageInfoPropertyIntValue(articleUsage, ArticleUsageInfoPropertyDef.CUSTOM_IMAGE_USER_ID, userId);
      }
    }
  }

  public removeImageFromConnections(connections: Connection[]): void {
    if (ArrayUtils.isEmpty(connections)) {
      return;
    }
    const connectionsWithImages: Connection[] = [];
    for (const connection of connections) {
      if (connection.imageBitmap || connection.backgroundBitmap) {
        connectionsWithImages.push(connection);
      }
    }
    if (connectionsWithImages.length > 0) {
      this.undoManager.pushUndo(new UndoRemoveImageConnectionsOperation(connectionsWithImages, this, this.containerService, this.labelingUtils));
      this.cleanConnectionImageCache(connectionsWithImages);
      ConnectionInfoUtils.removeImagesFromConnectionCubes(connectionsWithImages);
      this.loadAndApplyImagesToConnections(connectionsWithImages);
    }
  }

  /**
   * Note: after execution of this method some connections that were selected can be removed after merging (recreated).
   * Use this method when all manipulations with selected connections are done.
   */
  public refreshConnectionCubesAsync(connectionCubes: Connection[]): Promise<any> {
    return lastValueFrom(this.refreshConnectionCubes(connectionCubes));
  }

  public refreshConnectionCubes(connectionCubes: Connection[]): Observable<any> {
    if (!connectionCubes) {
      return of(null);
    }

    const processedArticlesGUIDs: string[] = [];
    for (const cube of connectionCubes) {
      cube.resetTexture();
      const identity: MetaElementUsageIdentityVO = cube.metaElementUsage.identity;
      if (processedArticlesGUIDs.indexOf(identity.articleUsageId) === -1) {
        processedArticlesGUIDs.push(identity.articleUsageId);
      }
    }

    return this.loadAndApplyImagesToConnectionsObservable(connectionCubes).pipe(
      map(() => {
        const containers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(processedArticlesGUIDs);
        for (const articleContainer of containers) {
          articleContainer.refreshTextureConnections(false);
        }
      })
    );
  }
}
